import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search, Sparkles, ArrowUpRight, ArrowRight, PlayCircle, BookOpen, Link, Target, Clock, Flame, Layers, Tag, Calendar, Zap, Pin, Lightbulb, X, Moon, RotateCcw, AlertCircle, Brain } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { FLAGS } from '../lib/flags';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { loadDashboardData } from '../services/dashboardDataService';
import { telemetry } from '../lib/telemetry';
import { useBehavioralLearning } from '../hooks/useBehavioralLearning';
import { getImageUrl } from '../utils/imageProxy';
import { getThumbnailUrl } from '../utils/thumbnailFallbacks';
import openaiService from '../services/openaiService';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import SmartSuggestionModal from '../components/modals/SmartSuggestionModal';
import contentService from '../services/contentService';
import personalizationService from '../services/personalizationService';
import { classifyTheme, getDominantTheme } from '../lib/themeClassifier';
import { THEME_MICRO_SUMMARIES } from '../lib/themeKeywords';
import suggestionsService from '../services/suggestionsService';

// Simple AI text generation helper
const ai = {
  async text(prompt) {
    try {
      const response = await openaiService.createChatCompletion([
        {
          role: 'system',
          content: 'You are Swirl, a personal insights narrator. Use a warm, clear, observational tone.'
        },
        {
          role: 'user',
          content: prompt
        }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 200
      });

      if (response.error || !response.data) {
        throw new Error(response.error || 'AI generation failed');
      }

      // API endpoint returns content directly in response.data (not full OpenAI response)
      const content = response.data;
      if (!content) {
        throw new Error('No content in AI response');
      }

      return content;
    } catch (error) {
      console.error('AI text generation failed:', error);
      throw error;
    }
  }
};

// Weekly Summary AI generator
async function generateAISummary({ topTopics = [], weeklyCount = 0, topicMap = {}, dominantTheme = 'General' }) {
  try {
    const topicsStr = topTopics.join(", ");
    const strongest = dominantTheme !== 'General' ? dominantTheme : (topTopics[0] || "your interests");
    const prompt = `
You are Swirl, a personal insights narrator.
Based on the user's week, provide:
1. A one-sentence teaser (max 15 words) that hooks them to read more.
2. A full summary paragraph (2-3 sentences) about their week.
3. Three actionable suggestions for next week.

Weekly items explored: ${weeklyCount}.
Top topics: ${topicsStr}.
User's dominant curiosity area this week: ${dominantTheme}.
Main theme: ${strongest}.

Respond in this exact JSON format:
{
  "teaser": "Your one-sentence teaser here",
  "summary": "Your full summary paragraph here",
  "suggestions": ["Suggestion 1", "Suggestion 2", "Suggestion 3"]
}
`;
    const response = await ai.text(prompt);
    
    // Parse JSON response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        teaser: parsed.teaser || `You explored ${weeklyCount} topics this week.`,
        summary: parsed.summary || `Your curiosity led you through ${topicsStr}.`,
        suggestions: parsed.suggestions || []
      };
    }
    
    // Fallback if JSON parsing fails
    return {
      teaser: `You explored ${weeklyCount} topics this week.`,
      summary: response.trim(),
      suggestions: []
    };
  } catch (error) {
    console.error('Weekly summary generation failed:', error);
    const topicsStr = topTopics.slice(0, 2).join(", ") || "various areas";
    return {
      teaser: weeklyCount === 0 
        ? 'Start exploring to see your weekly insights.' 
        : `You explored ${weeklyCount} topics this week.`,
      summary: weeklyCount === 0
        ? 'You did not explore any new topics this week.'
        : `You explored ${weeklyCount} topics this week, with your attention focused on ${topicsStr}.`,
      suggestions: []
    };
  }
}

// Momentum V2: Compute metrics from items
function computeMomentumMetrics(items = []) {
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  
  // Get items from this week and last week
  const thisWeekItems = items.filter(item => {
    const createdAt = new Date(item.created_at);
    return createdAt >= oneWeekAgo && createdAt <= now;
  });
  
  const lastWeekItems = items.filter(item => {
    const createdAt = new Date(item.created_at);
    return createdAt >= twoWeeksAgo && createdAt < oneWeekAgo;
  });
  
  const totalThisWeek = thisWeekItems.length;
  const totalLastWeek = lastWeekItems.length;
  
  // Calculate week delta - clear numeric language for all cases
  let weekDeltaPercent = 0;
  let weekDeltaText = 'No items yet';
  
  if (totalLastWeek === 0 && totalThisWeek === 0) {
    // Case 1: still nothing
    weekDeltaText = 'No items yet';
    weekDeltaPercent = 0;
  } else if (totalLastWeek === 0 && totalThisWeek > 0) {
    // Case 2: first week of activity
    weekDeltaText = `From 0 to ${totalThisWeek} item${totalThisWeek !== 1 ? 's' : ''}`;
    weekDeltaPercent = 100;
  } else {
    // Case 3: normal comparison
    const percent = ((totalThisWeek - totalLastWeek) / totalLastWeek) * 100;
    const sign = percent > 0 ? '+' : percent < 0 ? '−' : '';
    const rounded = Math.round(Math.abs(percent));
    weekDeltaPercent = Math.round(percent);
    weekDeltaText = `${sign}${rounded}% (${totalLastWeek} → ${totalThisWeek})`;
  }
  
  // Count items per day of week (0 = Sunday, 1 = Monday, etc.)
  const dayCountsRaw = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
  const hourCounts = new Array(24).fill(0);
  const themeCounts = {};
  
  thisWeekItems.forEach(item => {
    const createdAt = new Date(item.created_at);
    const dayOfWeek = createdAt.getDay(); // 0 = Sunday
    dayCountsRaw[dayOfWeek]++;
    
    const hour = createdAt.getHours();
    hourCounts[hour]++;
    
    // Extract themes from item
    const topics = [
      ...(item.auto_tags?.topics || []),
      ...(item.meta?.ai_analysis?.topics || []),
      ...(item.primary_tags || [])
    ];
    
    topics.forEach(topic => {
      if (topic && typeof topic === 'string') {
        const theme = classifyTheme([topic.toLowerCase()]);
        if (theme && theme !== 'General') {
          themeCounts[theme] = (themeCounts[theme] || 0) + 1;
        }
      }
    });
  });
  
  // Convert to Mo-Su order (shift Sunday to end)
  const dailyCounts = [...dayCountsRaw.slice(1), dayCountsRaw[0]]; // Mo, Tu, We, Th, Fr, Sa, Su
  
  // Find most/least active day
  const dayNamesShort = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const dayNamesFull = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  let maxDay = 0, minDay = 0, maxCount = dailyCounts[0], minCount = dailyCounts[0];
  
  dailyCounts.forEach((count, i) => {
    if (count > maxCount) {
      maxCount = count;
      maxDay = i;
    }
    if (count < minCount) {
      minCount = count;
      minDay = i;
    }
  });
  
  // Find peak upload hour
  let peakHour = 0, peakHourCount = 0;
  hourCounts.forEach((count, hour) => {
    if (count > peakHourCount) {
      peakHourCount = count;
      peakHour = hour;
    }
  });
  
  // Get top 2 rising themes
  const sortedThemes = Object.entries(themeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([theme]) => theme);
  
  const hasEnoughData = totalThisWeek >= 5;
  
  return {
    totalItemsThisWeek: totalThisWeek,
    totalItemsLastWeek: totalLastWeek,
    weekDeltaPercent,
    weekDeltaText,
    topTwoRisingThemes: sortedThemes,
    mostActiveDay: hasEnoughData ? dayNamesFull[maxDay] : null,
    mostActiveDayShort: hasEnoughData ? dayNamesShort[maxDay] : null,
    leastActiveDay: hasEnoughData ? dayNamesFull[minDay] : null,
    peakUploadHour: hasEnoughData ? peakHour : null,
    dailyCounts,
    hasEnoughData
  };
}

// Momentum V2: Generate micro AI insight
async function generateMomentumInsight(metrics) {
  if (!metrics.hasEnoughData) {
    return "";
  }
  
  try {
    // Convert peak hour to 12-hour format for AI prompt
    const peakHour12 = (() => {
      const hour = metrics.peakUploadHour;
      if (hour === null) return 'various times';
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${hour12}:00 ${period}`;
    })();
    
    const prompt = `Summarize this user's weekly activity in ONE short, punchy sentence (max 15 words).
    
Metrics:
- Items this week: ${metrics.totalItemsThisWeek}
- Change from last week: ${metrics.weekDeltaText}
- Most active day: ${metrics.mostActiveDay}
- Peak hour: ${peakHour12}
- Rising themes: ${metrics.topTwoRisingThemes.join(', ') || 'Various'}

Be observational and encouraging. No fluff. Text only, no quotes.`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a concise personal insights narrator. One sentence only.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 50
    });

    if (response.error || !response.data) {
      return "";
    }

    return response.data.trim().replace(/^["']|["']$/g, ''); // Remove any quotes
  } catch (error) {
    console.error('Momentum insight generation failed:', error);
    return "";
  }
}

// Smart Suggestions V2: Rule-based scoring system
// Uses suggestionsService for behavior-driven suggestions
// Returns ONE top-scoring suggestion based on:
// - Burnout detection (< 30% of weekly average)
// - Format clustering (10+ same format in 7 days)
// - Time-of-day patterns (same hour 3+ days/week)
// - Project accumulation (8+ items with similar metadata)
// - Long gaps (72+ hours no uploads)
// - Growth spikes (actionable only)

export default function SelfDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredDay, setHoveredDay] = useState(null);
  
  // Phase 9.5.2b: Enable behavioral learning
  useBehavioralLearning(user?.id);

  // Stage 6.5 Part 2: Track dashboard render
  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const loadTime = performance.now() - startTime;
      setTimeout(() => {
        telemetry.trackDashboardRender(
          Math.round(loadTime),
          6, // cardCount - V1 cards
          5  // interestsCount - approximate
        );
      }, 100);
    };
  }, []);

  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // V1 Data states - ONLY strong signals (no entities)
  const [recentItems, setRecentItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // All items for suggestions
  const [learningRhythm, setLearningRhythm] = useState(null);
  const [curiosityData, setCuriosityData] = useState(null);
  const [topTopicsMonth, setTopTopicsMonth] = useState([]);
  const [curiosityDistribution, setCuriosityDistribution] = useState([]);
  const [swirls, setSwirls] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState("");
  const [weeklySuggestions, setWeeklySuggestions] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  
  // Momentum V2 state
  const [momentumMetrics, setMomentumMetrics] = useState({
    totalItemsThisWeek: 0,
    totalItemsLastWeek: 0,
    weekDeltaPercent: 0,
    weekDeltaText: 'No items yet',
    topTwoRisingThemes: [],
    mostActiveDay: null,
    mostActiveDayShort: null,
    leastActiveDay: null,
    peakUploadHour: null,
    dailyCounts: [0, 0, 0, 0, 0, 0, 0], // Mo-Su
    hasEnoughData: false
  });
  
  const [momentumInsight, setMomentumInsight] = useState("");
  
  // Smart Suggestions state
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [pinnedSuggestions, setPinnedSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [insightsTab, setInsightsTab] = useState('insights'); // 'insights' or 'pinned'

  useEffect(() => {
    console.log('✅ Insights Dashboard mounted (V1 - Strong Data Only)');
    
    if (!FLAGS.SELF_DASHBOARD_ENABLED) {
      return;
    }

    const loadInsightsData = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        // Load unified data from dashboard service
        const unifiedData = await loadDashboardData(user.id, true);
        
        console.log(`✅ V1 Insights data loaded:`, {
          items: unifiedData.items.length,
          swirls: unifiedData.swirls.length,
          queries: unifiedData.queries.length,
        });

        // Store swirls (for "Continue With These Themes")
        setSwirls(unifiedData.swirls || []);

        // Set all items (for predictions)
        setAllItems(unifiedData.items || []);
        
        // Expose items for debugging (dev only)
        if (import.meta.env?.DEV && typeof window !== 'undefined') {
          window.__SWIRL_DEBUG_ITEMS__ = unifiedData.items || [];
          console.log('%c[Debug] Items exposed as window.__SWIRL_DEBUG_ITEMS__', 'color: #8b5cf6');
        }

        // Set recent items (for "What You've Been Exploring")
        const recent = unifiedData.items.slice(0, 12);
        setRecentItems(recent);

        // Process learning rhythm (for "Your Momentum")
        processLearningRhythm(unifiedData.queries, unifiedData.items);
        
        // Process curiosity distribution (for "Where Your Curiosity Lives")
        processCuriosityData(unifiedData.items);

        // Load recent queries (for "What You've Been Searching")
        const uniqueQueries = [];
        const seen = new Set();
        for (const q of unifiedData.queries || []) {
          const raw = q.query || q.text || q.message;
          const queryText = raw ? raw.toLowerCase().trim() : null;
          if (queryText && !seen.has(queryText)) {
            seen.add(queryText);
            uniqueQueries.push(q);
          }
          if (uniqueQueries.length === 8) break;
        }
        setRecentQueries(uniqueQueries);
        
      } catch (error) {
        console.error('Error loading insights data:', error);
        setRecentItems([]);
        setSwirls([]);
        setRecentQueries([]);
      } finally {
        setLoading(false);
      }
    };

    loadInsightsData();
  }, [user?.id]);


  // Process learning rhythm from queries, fallback to items
  const processLearningRhythm = (queries, items) => {
    try {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      // MOMENTUM V2: Compute full metrics from items
      const metrics = computeMomentumMetrics(items || []);
      setMomentumMetrics(metrics);
      
      // Try to use queries first for learning rhythm
      if (queries && queries.length > 0) {
        const weeklyQueries = queries.filter(q => {
          const queryDate = new Date(q.created_at);
          return queryDate >= weekAgo;
        });

        const topics = new Set();
        weeklyQueries.forEach(query => {
          const raw = query.query || query.text || query.message;
          const terms = raw?.split(' -> ') || [];
          terms.forEach(term => {
            if (term.trim()) topics.add(term.trim().toLowerCase());
          });
        });

        setLearningRhythm({
          weeklyCount: metrics.totalItemsThisWeek || weeklyQueries.length || 0,
          topics: Array.from(topics).slice(0, 3)
        });
        return;
      }

      // Fallback: Use items from last week
      if (items && items.length > 0) {
        const weeklyItems = items.filter(item => {
          const itemDate = new Date(item.created_at);
          return itemDate >= weekAgo;
        });

        const topics = new Set();
        weeklyItems.forEach(item => {
          const itemTopics = item.auto_tags?.topics || [];
          itemTopics.forEach(topic => {
            if (topic) topics.add(topic.toLowerCase());
          });
        });

        setLearningRhythm({
          weeklyCount: metrics.totalItemsThisWeek || weeklyItems.length || 0,
          topics: Array.from(topics).slice(0, 3)
        });
        return;
      }

      setLearningRhythm({ weeklyCount: 0, topics: [] });
    } catch (error) {
      console.error('Error processing learning rhythm:', error);
      setLearningRhythm({ weeklyCount: 0, topics: [] });
    }
  };


  // Process curiosity distribution from items auto_tags.topics
  const processCuriosityData = (items) => {
    try {
      const themeCounts = {};
      
      items.forEach(item => {
        let topics = [];
        
        if (item.auto_tags && typeof item.auto_tags === 'object') {
          topics = item.auto_tags.topics || item.auto_tags.themes || [];
        }
        
        if (topics.length === 0 && item.ai_analysis && typeof item.ai_analysis === 'object') {
          topics = item.ai_analysis.topics || item.ai_analysis.tags || [];
        }
        
        topics.forEach(topic => {
          if (topic && typeof topic === 'string') {
            const normalizedTopic = topic.toLowerCase().trim();
            if (normalizedTopic.length > 0) {
              themeCounts[normalizedTopic] = (themeCounts[normalizedTopic] || 0) + 1;
            }
          }
        });
      });

      const sortedCuriosity = Object.entries(themeCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([theme, count]) => {
          const capitalized = theme.charAt(0).toUpperCase() + theme.slice(1);
          return [capitalized, count];
        });

      const topThemes = sortedCuriosity.slice(0, 6).reduce((acc, [theme, count]) => {
        acc[theme] = count;
          return acc;
        }, {});

      setCuriosityData(topThemes);
      setTopTopicsMonth(sortedCuriosity.slice(0, 3));
      setCuriosityDistribution(sortedCuriosity.slice(0, 6));
      
      return themeCounts;
    } catch (error) {
      console.error('Error processing curiosity data:', error);
      setCuriosityData({});
      setTopTopicsMonth([]);
      setCuriosityDistribution([]);
      return {};
    }
  };

  // Generate weekly summary when data is ready
  useEffect(() => {
    async function runSummary() {
      if (!curiosityData || !learningRhythm) return;

      // Use classifier to determine dominant theme from curiosity data
      const topTopics = Object.keys(curiosityData).slice(0, 5);
      const dominantTheme = getDominantTheme(topTopics);

      const result = await generateAISummary({
        topTopics,
        weeklyCount: learningRhythm.weeklyCount || 0,
        topicMap: curiosityData,
        dominantTheme
      });
      setWeeklySummary(result);
      setWeeklySuggestions(result.suggestions || []);
    }

    runSummary();
  }, [curiosityData, learningRhythm]);

  // Momentum V2: Generate AI insight after metrics are computed
  useEffect(() => {
    async function runMomentumInsight() {
      if (!momentumMetrics.hasEnoughData) {
        setMomentumInsight("");
        return;
      }
      
      const insight = await generateMomentumInsight(momentumMetrics);
      setMomentumInsight(insight);
    }

    runMomentumInsight();
  }, [momentumMetrics]);

  // Smart Suggestions V2: Load top suggestion from rule-based scoring service
  useEffect(() => {
    async function loadSuggestions() {
      if (!user?.id) {
        setSmartSuggestions([]);
        setSuggestionsLoading(false);
        return;
      }

      try {
        setSuggestionsLoading(true);
        
        // Force fresh evaluation in development (V4 insight engine)
        const forceRefresh = import.meta.env?.DEV || false;
        console.log('%c[Insights] Loading Smart Insights...', 'color: #8b5cf6; font-weight: bold');
        console.log(`  Items available: ${allItems?.length || 0}`);
        console.log(`  Force refresh: ${forceRefresh}`);
        
        const suggestions = await suggestionsService.getSuggestionsForUser(
          user.id,
          allItems || [],
          momentumMetrics,
          forceRefresh // Force fresh in dev
        );
        
        setSmartSuggestions(suggestions || []);
        
        // Also load pinned suggestions
        const pinned = await suggestionsService.getPinnedSuggestions(user.id);
        setPinnedSuggestions(pinned);
      } catch (error) {
        console.error('Failed to load suggestions:', error);
        setSmartSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }

    loadSuggestions();
  }, [user?.id, allItems, momentumMetrics]);

  // Feature flag guard
  if (!FLAGS.SELF_DASHBOARD_ENABLED) {
    return (
      <div className="min-h-screen bg-[#F8F8F9] flex items-center justify-center">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-3">Insights</h1>
          <p className="mt-3 text-neutral-600">
            This feature is disabled. Set <code className="px-2 py-1 bg-neutral-100 rounded">VITE_SELF_DASHBOARD_ENABLED=true</code> to preview.
          </p>
        </div>
      </div>
    );
  }

  const placeholderThemes = [
    { title: 'Sheclies Hatme', topics: ['Content', 'Mussowa'] },
    { title: 'Belves Alvahic', topics: ['Repistey', 'Nasty'] }
  ];

  const themeCards = swirls.slice(0, 2).map((swirl, idx) => ({
    id: swirl.id || `theme-${idx}`,
    title: swirl.title || placeholderThemes[idx]?.title || `Theme ${idx + 1}`,
    topics:
      (Array.isArray(swirl.topics) && swirl.topics.length > 0
        ? swirl.topics.slice(0, 2)
        : placeholderThemes[idx]?.topics) || [],
    data: swirl
  }));

  while (themeCards.length < 2) {
    const idx = themeCards.length;
    themeCards.push({
      id: `placeholder-${idx}`,
      title: placeholderThemes[idx]?.title || `Theme ${idx + 1}`,
      topics: placeholderThemes[idx]?.topics || [],
      data: null
    });
  }

  const themeCardStyles = [
    { bg: 'bg-[#E9DEFF]', text: 'text-gray-900', chip: 'text-gray-600', link: 'text-gray-700', isDark: false },
    { bg: 'bg-gray-900', text: 'text-white', chip: 'text-gray-300', link: 'text-gray-200', isDark: true }
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="fixed left-16 top-8 w-8 h-8 bg-white/80 backdrop-blur-xl rounded-r-xl flex items-center justify-center text-gray-600 hover:bg-white/90 transition-all duration-300 z-[60] shadow-lg border border-white/20"
        >
          <ChevronRight size={16} />
        </button>
      )}

      {/* Main Content */}
      <main 
        className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`} 
        style={{ minHeight: '100vh' }}
      >
        <div id="insights-content">
          <div className="min-h-screen bg-[#F8F8F9] text-neutral-900 p-5">
            <div className="max-w-6xl mx-auto space-y-5">

              {/* Header */}
              <header className="mb-4">
                <h1 className="text-4xl font-serif text-neutral-900 mb-1">
                  Your <span className="text-[#7A47F5]">Insights</span>
              </h1>
                <p className="text-base text-neutral-500">
                A look at where your curiosity has been going.
              </p>
            </header>

              {/* Top Row: Weekly Summary + Your Momentum */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column: Weekly Summary + What You've Been Exploring */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Weekly Summary */}
                  <div 
                    className="relative overflow-hidden rounded-2xl h-28 shadow-[0_4px_12px_rgba(0,0,0,0.04)] cursor-pointer group"
                    onClick={() => weeklySummary && setShowSummaryModal(true)}
                  >
                    {/* Background Image */}
                    <img 
                      src="/assets/images/insights%20image%20header.png" 
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      onError={(e) => {
                        console.error('Image not found at:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Subtle overlay to ensure text readability */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent pointer-events-none z-0"></div>
                    <div className="flex flex-col justify-between h-full p-5 relative z-10">
                                  <div className="flex-1">
                        <h2 className="text-xl font-semibold text-black mb-1 tracking-tight">Your Weekly Summary</h2>
                        <p className="text-sm text-black leading-relaxed">
                          {typeof weeklySummary === 'object' 
                            ? weeklySummary.teaser 
                            : (weeklySummary || "Generating your weekly summary...")}
                        </p>
                        </div>
                      {weeklySummary && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-purple-600 group-hover:text-purple-700 transition-colors flex items-center gap-1">
                            Read more
                            <ArrowRight size={12} />
                          </span>
                        </div>
                      )}
                  </div>
                  </div>

                  {/* Where Your Curiosity Lives + What You've Been Searching */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Left Column: Where Your Curiosity Lives */}
                      {curiosityData && Object.keys(curiosityData).length > 0 && (() => {
                        const sortedEntries = Object.entries(curiosityData).sort((a, b) => b[1] - a[1]);
                        const curiosityDistribution = sortedEntries.slice(0, 3);
                        const total = Object.values(curiosityData).reduce((a, b) => a + b, 0);
                              return (
                          <div className="bg-white rounded-2xl p-4 border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                            <h3 className="text-base font-semibold text-gray-900 mb-3 tracking-tight">Where Your Curiosity Lives</h3>
                            <div className="space-y-3">
                              {curiosityDistribution.map(([topic, count], i) => {
                                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                if (!topic || pct === 0 || isNaN(pct)) return null;
                                return (
                                  <div key={topic}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-[12px] font-medium text-gray-700">{topic}</span>
                                      <span className="text-[11px] text-gray-400 font-medium tabular-nums">{pct}%</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                      <div 
                                        className="h-full rounded-full bg-gradient-to-r from-violet-300 to-purple-400 opacity-80"
                                        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                                      />
                                    </div>
                                  </div>
                              );
                              }).filter(Boolean)}
                        </div>
                          </div>
                              );
                            })()}
                        
                    {/* Right Column: What You've Been Searching */}
                    <div className="bg-white rounded-2xl p-4 border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <h3 className="text-base font-semibold text-gray-900 mb-3 tracking-tight">What You've Been Searching</h3>
                      {recentQueries.length === 0 ? (
                        <p className="text-neutral-400 text-sm">No recent searches yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {recentQueries.map((q, i) => {
                            // Rotate through elegant color combinations
                            const colorVariants = [
                              'bg-gradient-to-r from-violet-50 to-purple-50 text-violet-700 border border-violet-100 hover:from-violet-100 hover:to-purple-100',
                              'bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border border-blue-100 hover:from-blue-100 hover:to-cyan-100',
                              'bg-gradient-to-r from-pink-50 to-rose-50 text-pink-700 border border-pink-100 hover:from-pink-100 hover:to-rose-100',
                              'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-100 hover:from-amber-100 hover:to-orange-100',
                              'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border border-emerald-100 hover:from-emerald-100 hover:to-teal-100',
                              ];
                              return (
                              <span
                                key={i}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 hover:shadow-sm ${colorVariants[i % colorVariants.length]}`}
                                onClick={() => window.location.href = `/swirlspace?q=${encodeURIComponent(q.query || q.text || q.message)}`}
                              >
                                {q.query || q.text || q.message}
                              </span>
                        );
                      })}
                    </div>
                      )}
                  </div>
                                </div>
                        
                  {/* What You've Been Exploring */}
                  <div className="bg-white rounded-2xl p-4 border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <h3 className="text-base font-semibold text-gray-900 mb-2.5 tracking-tight">What You've Been Exploring</h3>
                    <div className="grid grid-cols-5 gap-2">
                      {recentItems.length > 0 ? (
                        recentItems.slice(0, 10).map((item, i) => {
                        const thumbUrl = getThumbnailUrl(item) || item.thumbnail_url || item.image_url || (item.source_type === 'image' ? item.url : null);
                        const proxiedUrl = thumbUrl ? getImageUrl({ image: thumbUrl, thumbnail_url: thumbUrl }) : null;
                          const gradientClasses = [
                            'bg-gradient-to-br from-pink-200 via-purple-200 to-cyan-200',
                            'bg-gradient-to-tr from-purple-200 via-pink-200 to-cyan-200',
                            'bg-gradient-to-bl from-cyan-200 via-purple-200 to-pink-200'
                          ];
                        
                        return (
                            <div
                            key={item.id || i}
                            onClick={() => {
                              // Track view and update context in background (non-blocking)
                              if (user?.id && item.id) {
                                contentService.trackItemView(item.id, user.id).catch(() => {});
                                personalizationService.updateContextFromItem(item).catch(() => {});
                              }
                              
                              // Map the item to modal format (same as Swirlspace)
                              const mappedItem = {
                                ...item,
                                id: item.id,
                                title: item.title || item.description || 'Untitled',
                                description: item.description,
                                url: item.url,
                                source_type: item.source_type || item.content_type,
                                contentType: item.source_type || item.content_type,
                                channel: item.channel || item.meta?.channel || (item.source_type ? item.source_type.charAt(0).toUpperCase() + item.source_type.slice(1) : 'Content'),
                                thumbnail_url: item.thumbnail_url || item.url || item.image_url,
                                analysis_data: item.analysis_data || item.meta?.analysis_data || {
                                  summary: item.meta?.summary || item.description,
                                  ocr: { text: item.extracted_text },
                                  colors: { primaryColor: item.primary_color, mood: item.color_mood, palette: item.color_palette },
                                  tagging: { primaryTags: item.primary_tags, allTags: item.auto_tags },
                                },
                                extracted_text: item.extracted_text,
                                auto_tags: item.auto_tags,
                                primary_tags: item.primary_tags,
                                primary_color: item.primary_color,
                                color_mood: item.color_mood,
                                color_palette: item.color_palette,
                                meta: item.meta,
                                created_at: item.created_at,
                                _source: item._source || (item.source_type === 'image' ? 'deepsearch.items' : 'instagram_content'),
                              };
                              
                              setSelectedContent(mappedItem);
                              setShowContentModal(true);
                            }}
                              className="aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                          >
                            {proxiedUrl ? (
                              <img 
                                src={proxiedUrl} 
                                alt="" 
                                  className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                    e.target.parentElement.className += ` ${gradientClasses[i % 3]}`;
                                }}
                              />
                              ) : (
                                <div className={`w-full h-full ${gradientClasses[i % 3]} flex items-center justify-center text-xl`}>
                              {item.source_type === 'video' ? '🎥' : item.source_type === 'note' ? '📝' : '📄'}
                            </div>
                              )}
                    </div>
                          );
                        })
                      ) : (
                        Array.from({ length: 10 }).map((_, i) => {
                          const gradientClasses = [
                            'bg-gradient-to-br from-pink-200 via-purple-200 to-cyan-200',
                            'bg-gradient-to-tr from-purple-200 via-pink-200 to-cyan-200',
                            'bg-gradient-to-bl from-cyan-200 via-purple-200 to-pink-200'
                          ];
                              return (
                            <div 
                              key={i} 
                              className={`aspect-square rounded-xl ${gradientClasses[i % 3]}`}
                            />
                          );
                        })
                      )}
                    </div>
                        </div>
                      </div>
                        
                {/* Right Column: Your Momentum + Top Topics + Continue With These Themes */}
                        <div className="space-y-4">
                  {/* Your Momentum V2 */}
                  <div className="bg-white rounded-2xl p-4 border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-semibold text-gray-900 tracking-tight">Your Momentum</h3>
                      {momentumMetrics.hasEnoughData && momentumMetrics.weekDeltaText !== 'No items yet' && (
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          momentumMetrics.weekDeltaText.startsWith('From 0')
                            ? 'bg-green-50 text-green-600'
                            : momentumMetrics.weekDeltaPercent >= 0 
                              ? 'bg-purple-50 text-purple-600' 
                              : 'bg-amber-50 text-amber-600'
                        }`}>
                          {momentumMetrics.weekDeltaText.startsWith('From 0') ? (
                            <Sparkles size={10} />
                          ) : momentumMetrics.weekDeltaPercent >= 0 ? (
                            <ArrowUpRight size={10} />
                          ) : (
                            <ArrowRight size={10} className="rotate-45" />
                          )}
                          {momentumMetrics.weekDeltaPercent >= 0 ? '+' : '−'}{Math.abs(momentumMetrics.weekDeltaPercent)}%
                        </div>
                      )}
                    </div>
                    <div className="mb-2">
                      <div className="text-3xl font-semibold text-gray-900 mb-1">
                        {momentumMetrics.totalItemsThisWeek || learningRhythm?.weeklyCount || 0}
                      </div>
                      <div className="text-[12px] text-gray-400 font-medium mb-2">items this week</div>
                      
                      {/* Sparkline Chart - V2 with real data */}
                            {(() => {
                        const days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
                        const dailyCounts = momentumMetrics.dailyCounts;
                        const maxCount = Math.max(...dailyCounts, 1);
                        
                        // Calculate Y positions based on real data (invert because SVG Y grows downward)
                        const yPositions = dailyCounts.map(count => 
                          Math.max(8, 56 - (count / maxCount) * 48) // Scale to 8-56 range
                        );
                        
                        // X positions for each day (7 days across 200px width)
                        const xPositions = [0, 33, 66, 100, 133, 166, 200];
                        
                        // Generate smooth path from real data points
                        const generateSmoothPath = (xPos, yPos) => {
                          if (xPos.length < 2) return '';
                          let path = `M ${xPos[0]},${yPos[0]}`;
                          for (let i = 0; i < xPos.length - 1; i++) {
                            const x1 = xPos[i];
                            const y1 = yPos[i];
                            const x2 = xPos[i + 1];
                            const y2 = yPos[i + 1];
                            const cpx1 = x1 + (x2 - x1) / 3;
                            const cpx2 = x1 + 2 * (x2 - x1) / 3;
                            path += ` C ${cpx1},${y1} ${cpx2},${y2} ${x2},${y2}`;
                          }
                          return path;
                        };
                        
                        const linePath = generateSmoothPath(xPositions, yPositions);
                        const areaPath = linePath + ` L 200,64 L 0,64 Z`;
                        
                        // Convert Y positions to percentages for tooltip
                        const yPercents = yPositions.map(y => (y / 64) * 100);
                        const xPercents = [0, 16.5, 33, 50, 66.5, 83, 100];
                                    
                                    return (
                          <div className="h-16 w-full mb-1 relative pt-5">
                            {/* Tooltip */}
                            {hoveredDay !== null && (
                              <div 
                                className="absolute z-20 pointer-events-none"
                                style={{
                                  left: `${xPercents[hoveredDay]}%`,
                                  top: `${(yPercents[hoveredDay] * 0.875) + 24 - 32}%`,
                                  transform: 'translateX(-50%)',
                                }}
                              >
                                <div className="bg-white rounded-lg shadow-lg border border-gray-100 px-3 py-1.5 text-[11px] font-semibold text-gray-900 whitespace-nowrap">
                                  {dailyCounts[hoveredDay]} item{dailyCounts[hoveredDay] !== 1 ? 's' : ''}
                                  </div>
                              </div>
                            )}
                            <svg width="100%" height="44" viewBox="0 0 200 64" preserveAspectRatio="none" className="w-full">
                              {/* Line path */}
                              <path
                                d={linePath}
                                fill="none"
                                stroke="url(#sparklineStroke)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                opacity="0.7"
                              />
                              {/* Area fill */}
                              <path
                                d={areaPath}
                                fill="url(#sparklineGradient)"
                              />
                              {/* Hover circle */}
                              {hoveredDay !== null && (
                                <circle
                                  cx={xPositions[hoveredDay]}
                                  cy={yPositions[hoveredDay]}
                                  r="4"
                                  fill="#A78BFA"
                                  stroke="white"
                                  strokeWidth="2"
                                  className="pointer-events-none"
                                />
                              )}
                              {/* Interactive hover zones */}
                              {days.map((day, i) => (
                                <rect
                            key={i}
                                  x={i === 0 ? 0 : xPositions[i] - 15}
                                  y="0"
                                  width="30"
                                  height="64"
                                  fill="transparent"
                                  onMouseEnter={() => setHoveredDay(i)}
                                  onMouseLeave={() => setHoveredDay(null)}
                                  className="cursor-pointer"
                                />
                              ))}
                              <defs>
                                <linearGradient id="sparklineStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#C4B5FD" />
                                  <stop offset="50%" stopColor="#A78BFA" />
                                  <stop offset="100%" stopColor="#C4B5FD" />
                                </linearGradient>
                                <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="#C4B5FD" stopOpacity="0.12" />
                                  <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.06" />
                                  <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0.01" />
                                </linearGradient>
                              </defs>
                            </svg>
                                  </div>
                              );
                            })()}
                      
                      {/* Day labels */}
                      <div className="flex items-center justify-between">
                        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, i) => (
                          <span 
                            key={i}
                            className={`text-[10px] font-medium ${
                              momentumMetrics.mostActiveDayShort === day 
                                ? 'text-purple-600' 
                                : 'text-gray-400'
                            }`}
                          >
                            {day}
                          </span>
                        ))}
                          </div>
                        </div>
                        
                    {/* Momentum V2: Details Section */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {momentumMetrics.hasEnoughData ? (
                        <>
                          <div className="text-[11px] space-y-3">
                            {/* Rising themes */}
                            {momentumMetrics.topTwoRisingThemes.length > 0 && (
                              <p className="text-gray-500">
                                Rising themes: <span className="font-medium text-gray-700">{momentumMetrics.topTwoRisingThemes.join(', ')}</span>
                              </p>
                            )}
                            
                            {/* Compact stats row */}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-gray-500">
                              <span>Most active <span className="font-medium text-gray-700">{momentumMetrics.mostActiveDay}</span></span>
                              <span className="text-gray-300">·</span>
                              <span>Least active <span className="font-medium text-gray-700">{momentumMetrics.leastActiveDay}</span></span>
                              <span className="text-gray-300">·</span>
                              <span>Peak <span className="font-medium text-gray-700">
                                {momentumMetrics.peakUploadHour !== null 
                                  ? (() => {
                                      const hour = momentumMetrics.peakUploadHour;
                                      const period = hour >= 12 ? 'PM' : 'AM';
                                      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                      return `${hour12} ${period}`;
                                    })()
                                  : '—'}
                              </span></span>
                        </div>
                            
                            {/* Week comparison */}
                            <p className="text-gray-500">
                              Change from last week: <span className={`font-medium ${
                                momentumMetrics.weekDeltaText === 'No items yet' 
                                  ? 'text-gray-600' 
                                  : momentumMetrics.weekDeltaText.startsWith('From 0')
                                    ? 'text-green-600'
                                    : momentumMetrics.weekDeltaPercent >= 0 
                                      ? 'text-purple-600' 
                                      : 'text-amber-600'
                              }`}>
                                {momentumMetrics.weekDeltaText}
                              </span>
                            </p>
                      </div>
                          
                          {/* AI Insight */}
                          {momentumInsight && (
                            <p className="mt-3 text-[11px] text-gray-500 italic leading-relaxed">
                              {momentumInsight}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-[11px] text-gray-400 text-center py-2">
                          Not enough data yet — keep exploring to unlock insights.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Smart Insights Card with Tabs */}
                  <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-100">
                      <button
                        onClick={() => setInsightsTab('insights')}
                        className={`flex-1 py-2.5 text-[12px] font-semibold transition-colors ${
                          insightsTab === 'insights' 
                            ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50/50' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Smart Insights
                      </button>
                      <button
                        onClick={() => setInsightsTab('pinned')}
                        className={`flex-1 py-2.5 text-[12px] font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                          insightsTab === 'pinned' 
                            ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50/50' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Pin size={12} />
                        Pinned
                        {pinnedSuggestions.length > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-600 rounded-full">
                            {pinnedSuggestions.length}
                          </span>
                        )}
                      </button>
                    </div>
                    
                    {/* Tab Content */}
                    <div className="p-4">
                      {insightsTab === 'insights' ? (
                        // Insights Tab
                        suggestionsLoading ? (
                          <div className="space-y-2">
                            {[1, 2].map(i => (
                              <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl animate-pulse">
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                                <div className="flex-1 space-y-1.5">
                                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                                  <div className="h-2.5 bg-gray-100 rounded w-4/5" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : smartSuggestions.length > 0 ? (
                          <div className="space-y-2">
                            {/* Always show 3 cards: top 2 insights + AI Predictions */}
                            {smartSuggestions.slice(0, 3).map((suggestion, idx) => {
                              const typeId = typeof suggestion.type === 'string' ? suggestion.type : (suggestion.type?.id || 'none');
                              
                              // V5 - 10 Final Insight Types + AI Predictions
                              const iconConfig = {
                                project_forming: { icon: Target, bg: 'bg-purple-50', iconBg: 'bg-purple-100', color: '#7c3aed' },
                                daily_rhythm: { icon: Clock, bg: 'bg-blue-50', iconBg: 'bg-blue-100', color: '#2563eb' },
                                theme_comeback: { icon: RotateCcw, bg: 'bg-pink-50', iconBg: 'bg-pink-100', color: '#db2777' },
                                deep_dive: { icon: Layers, bg: 'bg-indigo-50', iconBg: 'bg-indigo-100', color: '#6366f1' },
                                streak_momentum: { icon: Flame, bg: 'bg-orange-50', iconBg: 'bg-orange-100', color: '#ea580c' },
                                time_pattern: { icon: Moon, bg: 'bg-violet-50', iconBg: 'bg-violet-100', color: '#8b5cf6' },
                                early_signs: { icon: Sparkles, bg: 'bg-teal-50', iconBg: 'bg-teal-100', color: '#0d9488' },
                                planning_signal: { icon: Calendar, bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', color: '#059669' },
                                content_spark: { icon: Zap, bg: 'bg-yellow-50', iconBg: 'bg-yellow-100', color: '#ca8a04' },
                                pattern_block: { icon: AlertCircle, bg: 'bg-amber-50', iconBg: 'bg-amber-100', color: '#d97706' },
                                ai_predictions: { icon: Brain, bg: 'bg-gradient-to-br from-violet-50 to-fuchsia-50', iconBg: 'bg-gradient-to-br from-violet-100 to-fuchsia-100', color: '#8b5cf6' },
                              };
                              const config = iconConfig[typeId] || { icon: Lightbulb, bg: 'bg-gray-50', iconBg: 'bg-gray-100', color: '#6b7280' };
                              const IconComponent = config.icon;
                              
                            return (
                                <div
                                  key={suggestion.id || idx}
                                  onClick={() => {
                                    setActiveSuggestion(suggestion);
                                    setShowSuggestionModal(true);
                                  }}
                                  className={`group flex items-center gap-3 p-2.5 ${config.bg} rounded-xl cursor-pointer hover:shadow-sm transition-all duration-200`}
                                >
                                  <div className={`w-8 h-8 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
                                    <IconComponent size={14} style={{ color: config.color }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[12px] font-semibold text-gray-900">
                                      {suggestion.title || suggestion.shortTitle}
                                    </p>
                                    <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                                      {suggestion.subtitle || 'Based on your activity'}
                                    </p>
                                  </div>
                                  <ChevronRight size={14} className="text-gray-400 group-hover:text-purple-500 flex-shrink-0 transition-colors" />
                                </div>
                            );
                          })}
                      </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center mx-auto mb-2">
                              <Brain size={14} className="text-purple-500" />
                            </div>
                            <p className="text-[11px] text-gray-500 font-medium">
                              Loading insights...
                            </p>
                          </div>
                        )
                      ) : (
                        // Pinned Tab
                        pinnedSuggestions.length > 0 ? (
                          <div className="space-y-2">
                            {pinnedSuggestions.map((pinned, idx) => {
                              const typeId = typeof pinned.type === 'string' ? pinned.type : (pinned.type?.id || 'none');
                              
                              // V5 - 10 Final Insight Types + AI Predictions (Pinned)
                              const iconConfig = {
                                project_forming: { icon: Target, bg: 'bg-purple-50', iconBg: 'bg-purple-100', color: '#7c3aed' },
                                daily_rhythm: { icon: Clock, bg: 'bg-blue-50', iconBg: 'bg-blue-100', color: '#2563eb' },
                                theme_comeback: { icon: RotateCcw, bg: 'bg-pink-50', iconBg: 'bg-pink-100', color: '#db2777' },
                                deep_dive: { icon: Layers, bg: 'bg-indigo-50', iconBg: 'bg-indigo-100', color: '#6366f1' },
                                streak_momentum: { icon: Flame, bg: 'bg-orange-50', iconBg: 'bg-orange-100', color: '#ea580c' },
                                time_pattern: { icon: Moon, bg: 'bg-violet-50', iconBg: 'bg-violet-100', color: '#8b5cf6' },
                                early_signs: { icon: Sparkles, bg: 'bg-teal-50', iconBg: 'bg-teal-100', color: '#0d9488' },
                                planning_signal: { icon: Calendar, bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', color: '#059669' },
                                content_spark: { icon: Zap, bg: 'bg-yellow-50', iconBg: 'bg-yellow-100', color: '#ca8a04' },
                                pattern_block: { icon: AlertCircle, bg: 'bg-amber-50', iconBg: 'bg-amber-100', color: '#d97706' },
                                ai_predictions: { icon: Brain, bg: 'bg-gradient-to-br from-violet-50 to-fuchsia-50', iconBg: 'bg-gradient-to-br from-violet-100 to-fuchsia-100', color: '#8b5cf6' },
                              };
                              const config = iconConfig[typeId] || { icon: Lightbulb, bg: 'bg-gray-50', iconBg: 'bg-gray-100', color: '#6b7280' };
                              const IconComponent = config.icon;
                              
                              return (
                                <div
                                  key={pinned.id || idx}
                                  className={`group flex items-center gap-3 p-2.5 ${config.bg} rounded-xl`}
                                >
                                  <div 
                            onClick={() => {
                                      setActiveSuggestion(pinned);
                                      setShowSuggestionModal(true);
                                    }}
                                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                  >
                                    <div className={`w-8 h-8 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
                                      <IconComponent size={14} style={{ color: config.color }} />
                      </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[12px] font-semibold text-gray-900">
                                        {pinned.title || pinned.shortTitle}
                                      </p>
                                      <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                                        {pinned.subtitle || pinned.whyShowing}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={async () => {
                                      const newPinned = await suggestionsService.unpinSuggestion(pinned.id, user?.id);
                                      setPinnedSuggestions(newPinned);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                              <Pin size={14} className="text-gray-400" />
                            </div>
                            <p className="text-[11px] text-gray-400">
                              Pin insights to save them here.
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>


                  {/* Continue With These Themes */}
                {swirls.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 border border-neutral-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <h3 className="text-base font-semibold text-gray-900 mb-3 tracking-tight">Continue With These Themes</h3>
                      <div className="space-y-2">
                      {swirls.slice(0, 3).map((swirl, i) => {
                          const icons = [Sparkles, BookOpen, PlayCircle];
                          const Icon = icons[i] || Sparkles;
                          const iconColors = [
                            'bg-purple-100 text-purple-600',
                            'bg-violet-100 text-violet-600',
                            'bg-fuchsia-100 text-fuchsia-600'
                          ];
                        
                        // Known theme names that map directly
                        const KNOWN_THEMES = ['Technology', 'Business', 'Creativity', 'HealthFitness', 'Psychology', 'Productivity', 'WealthCareer', 'Lifestyle'];
                        
                        // First check if swirl.title IS a known theme name
                        const titleAsTheme = KNOWN_THEMES.find(t => 
                          t.toLowerCase() === swirl.title?.toLowerCase() ||
                          swirl.title?.toLowerCase().includes(t.toLowerCase())
                        );
                        
                        // Collect all topics from swirl and preview items for classification
                        const allTopics = [
                          ...(swirl.topics || []),
                          ...(swirl.preview?.flatMap(item => item.auto_tags?.topics || []) || []),
                          ...(swirl.preview?.flatMap(item => item.meta?.ai_analysis?.topics || []) || []),
                          ...(swirl.preview?.flatMap(item => item.primary_tags || []) || []),
                        ];
                        
                        // Use title if it's a known theme, otherwise classify from topics
                        const primaryTheme = titleAsTheme || classifyTheme(allTopics) || "General";
                        
                        // CEO-energy subtitle logic - use classifier result for micro-summary
                        const subtitle = 
                          THEME_MICRO_SUMMARIES[primaryTheme] ||
                          swirl.topics?.[0] ||
                          swirl.summary ||
                          swirl.description ||
                          "Insights curated around your interests.";
                        
                        return (
                            <div
                            key={swirl.id || i} 
                            onClick={() => {
                              // Navigate using classified theme for accurate filtering
                              if (primaryTheme && primaryTheme !== "General") {
                                const url = `/swirlspace?theme=${encodeURIComponent(primaryTheme)}`;
                                navigate(url);
                              } else if (swirl.id) {
                                const url = `/swirlspace?swirl=${swirl.id}`;
                                navigate(url);
                              } else {
                                navigate(`/swirlspace`);
                              }
                            }}
                              className="group flex items-center gap-3 p-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors"
                            >
                                <div className={`w-8 h-8 rounded-full ${iconColors[i % 3]} flex items-center justify-center shrink-0`}>
                                  <Icon size={14} />
                                </div>
                              <div className="flex-1 min-w-0">
                                  <h4 className="text-[12px] font-semibold text-gray-900">
                                  {swirl.title || `Theme ${i + 1}`}
                                  </h4>
                                  <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                                    {subtitle}
                                  </p>
                                  </div>
                                <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" />
                              </div>
                        );
                      })}
                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

              </div>
          </div>
        </div>
      </main>

      {/* Content Detail Modal - Same as Swirlspace */}
      <ContentDetailModal
        isOpen={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
      />

      {/* Smart Suggestion Modal */}
      <SmartSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => {
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        suggestion={activeSuggestion}
        allSuggestions={smartSuggestions}
        relatedItems={activeSuggestion?.relatedItems || []}
        momentumData={momentumMetrics}
        isPinned={activeSuggestion ? pinnedSuggestions.some(p => p.id === activeSuggestion.id) : false}
        onPin={async (suggestion) => {
          const newPinned = await suggestionsService.pinSuggestion(suggestion, user?.id);
          setPinnedSuggestions(newPinned);
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
          setInsightsTab('pinned'); // Switch to pinned tab after pinning
        }}
        onUnpin={async (suggestionId) => {
          const newPinned = await suggestionsService.unpinSuggestion(suggestionId, user?.id);
          setPinnedSuggestions(newPinned);
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onSnooze={(suggestionId, duration) => {
          suggestionsService.snoozeSuggestion(suggestionId, duration);
          // Remove from current list
          setSmartSuggestions(prev => prev.filter(s => s.id !== suggestionId));
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onDismiss={(suggestionId) => {
          suggestionsService.dismissSuggestion(suggestionId);
          // Remove from current list
          setSmartSuggestions(prev => prev.filter(s => s.id !== suggestionId));
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onSelectSuggestion={(suggestion) => {
          setActiveSuggestion(suggestion);
        }}
      />

      {/* Weekly Summary Modal */}
      {showSummaryModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSummaryModal(false)}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />
          
          {/* Modal */}
                          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Header with gradient */}
            <div className="relative h-24 bg-gradient-to-br from-purple-100 via-pink-50 to-cyan-50">
              <div 
                className="absolute inset-0 bg-[url('/assets/images/insights%20image%20header.png')] bg-cover opacity-20"
                style={{ backgroundPosition: '-20% center' }}
              />
              <div className="absolute bottom-4 left-6">
                <h2 className="text-2xl font-serif text-[#7A47F5] tracking-tight">Your Week in Review</h2>
                                </div>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/60 hover:bg-white/80 flex items-center justify-center transition-colors shadow-sm"
              >
                <span className="text-neutral-600 text-lg">×</span>
              </button>
                              </div>
                              
                              {/* Content */}
            <div className="p-6">
              {/* Summary */}
              <div className="mb-6">
                <p className="text-neutral-700 leading-relaxed">
                  {typeof weeklySummary === 'object' ? weeklySummary.summary : weeklySummary}
                      </p>
                    </div>
              
              {/* Suggestions */}
              {weeklySuggestions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">
                    Suggestions for Next Week
                                </h3>
                  <div className="space-y-2">
                    {weeklySuggestions.map((suggestion, i) => (
                      <div 
                        key={i}
                        className="flex items-start gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                          <Sparkles size={12} className="text-white" />
                                  </div>
                        <p className="text-sm text-neutral-700 leading-relaxed">{suggestion}</p>
                              </div>
                    ))}
                            </div>
                    </div>
              )}
              
              {/* Stats */}
              {learningRhythm && (
                <div className="mt-6 pt-4 border-t border-neutral-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Items explored this week</span>
                    <span className="font-semibold text-purple-600">{learningRhythm.weeklyCount || 0}</span>
                    </div>
              </div>
            )}
          </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
